import React from "react";
import styled from "styled-components";
import { colors } from "utils";
import { studentExecutiveSummary } from "./ParsedSummary/studentExecutiveSummary";
import { executiveSummary } from "./ParsedSummary/executiveSummary";
import { quotes } from "./ParsedSummary/quotes";
import { nextSteps } from "./ParsedSummary/nextSteps";
import { questionBreakdown } from "./ParsedSummary/questionBreakdown";
import { sessionHeadlines } from "./ParsedSummary/sessionHeadlines";
import { studentHighlights } from "./ParsedSummary/studentHighlights";
import { IncidentCountsSummary } from "../shared/criticalIncidentsUi";
import { CollabLevelInfo } from "../shared/CollabLevelInfo";
import { LEVEL_COLORS } from "components/shared/TagLevelSmall/TagLevelSmall";
import { AnswerMark } from "../shared/QuestionMetrics";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import info2 from "assets/info2.svg";

const InlineInfoIcon = styled.img`
  width: 14px;
  height: 14px;
  margin-left: 4px;
  cursor: pointer;
  vertical-align: text-bottom;
`;

// The level badges name their foreground `text`; the pill calls it `fg`.
const toneOf = ({ bg, border, text }) => ({ bg, border, fg: text });

export const findProp = (obj, searchTerm) => {
  if (!obj) return undefined;
  const searchLower = searchTerm.toLowerCase();
  const key = Object.keys(obj).find(k => k.toLowerCase().includes(searchLower));
  return key ? obj[key] : undefined;
};

export const getProp = (obj, searchTerms) => {
  if (!obj) return undefined;
  const terms = Array.isArray(searchTerms) ? searchTerms : [searchTerms];

  for (const term of terms) {
    const value = findProp(obj, term);
    if (value !== undefined) return value;
  }
  return undefined;
};

const safeContent = (content) => {
  if (content === null || content === undefined) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'number' || typeof content === 'boolean') return String(content);
  return ''; // Don't render objects
};

// Render content with text before last colon in bold
const renderContentWithBoldColon = (content, existingLabel) => {
  const text = safeContent(content);

  // If no content but has label, just render the label
  if (!text) {
    if (existingLabel) {
      return <strong>{existingLabel}</strong>;
    }
    return null;
  }

  // If there's already an explicit label, render content as-is (no colon bolding)
  if (existingLabel) {
    return (
      <>
        <strong>{existingLabel} </strong>
        {text}
      </>
    );
  }

  // No explicit label: auto-detect label from content by finding last colon
  const lastColonIndex = text.lastIndexOf(':');
  if (lastColonIndex !== -1) {
    const beforeColon = text.substring(0, lastColonIndex + 1);
    const afterColon = text.substring(lastColonIndex + 1);
    return (
      <>
        <strong>{beforeColon}</strong>{afterColon}
      </>
    );
  }

  return text;
};

// `deterministic` carries the backend-computed values the report shows instead
// of the LLM's, already gated by flag + cutoff. Empty when the session is
// outside the gate, which leaves every section on its pre-cutoff behaviour.
export const parseSummaryData = (summaryData, questionsOnly = false, currentQuestionIndex, isShortSession = false, studentsOnly = false, currentStudentIndex, deterministic = {}) => {
  if (!summaryData) return null;

  const sections = [];

  if (studentsOnly) {
    studentExecutiveSummary(summaryData, sections, false, currentStudentIndex, deterministic)
    return sections;
  }

  // Extract sessionMetadata from summaryData
  const sessionMetadata = getProp(summaryData, ['sessionMetadata', 'session_metadata']);

  const sessionOverview = getProp(summaryData, ['session_overview', 'overview']);
  if (sessionOverview && typeof sessionOverview === 'string') {
    sections.push({
      type: 'title',
      content: 'Session Overview'
    });
    sections.push({
      type: 'text',
      content: sessionOverview
    });
  }

  if (questionsOnly) {
    questionBreakdown(summaryData, sections, currentQuestionIndex, deterministic)
  } else {
    sessionHeadlines(summaryData, sections, sessionMetadata, deterministic)

    // Only show headlines for short sessions (< 3 min)
    if (!isShortSession) {
      nextSteps(summaryData, sections)
      quotes(summaryData, sections, deterministic)
      executiveSummary(summaryData, sections)
      studentHighlights(summaryData, sections, isShortSession, deterministic)
    }
  }


  return sections;
};

// Told to the teacher in their own terms: no key names, no version numbers, no
// JSON vocabulary. The tone follows the existing suppression notice, which is
// this product's house style for saying a report is limited.
export const INCOMPLETE_REPORT_NOTICE =
  "Some of this session's results are still being prepared, so this report is "
  + "incomplete. The written summary below is complete, but the scores and "
  + "question-by-question results are not shown. Please check back shortly.";

// Under v9 the session's facts — the scores, the levels, the per-question
// results — come from the backend rather than from the written summary. Without
// them the report is incomplete by construction, so it says so rather than
// quietly rendering a thinner report that looks whole.
//
// A report carrying no promptStage is the shape production serves today: it
// writes its own facts and is never incomplete for want of the backend's.
const needsDeterministicData = (summary) =>
  typeof summary?.promptStage === 'string' && summary.promptStage.length > 0;

// `sections` lets a caller with a different report shape (the Lesson Report
// console) reuse this renderer by handing it an already-built section list.
export const ParsedSummary = ({ summary, sections: presetSections, questionsOnly, isShortSession, currentQuestionIndex, skipTitle, studentsOnly, currentStudentIndex, deterministic, onViewIncidents }) => {
  let sections = presetSections || parseSummaryData(summary, questionsOnly, currentQuestionIndex, isShortSession, studentsOnly, currentStudentIndex, deterministic);
  const isIncomplete = needsDeterministicData(summary) && !deterministic?.enabled;

  // Filter out title-main section if skipTitle is true
  if (skipTitle && sections) {
    sections = sections.filter(section => section.type !== 'title-main');
  }

  const groupedSections = [];
  let currentBulletGroup = [];
  let currentOrderedGroup = [];
  let currentSubBulletGroup = [];
  let currentSubSubBulletGroup = [];

  const flushGroups = () => {
    if (currentSubSubBulletGroup.length > 0) {
      groupedSections.push({ type: 'sub-sub-bullet-group', items: currentSubSubBulletGroup });
      currentSubSubBulletGroup = [];
    }
    if (currentSubBulletGroup.length > 0) {
      groupedSections.push({ type: 'sub-bullet-group', items: currentSubBulletGroup });
      currentSubBulletGroup = [];
    }
    if (currentBulletGroup.length > 0) {
      groupedSections.push({ type: 'bullet-group', items: currentBulletGroup });
      currentBulletGroup = [];
    }
    if (currentOrderedGroup.length > 0) {
      groupedSections.push({ type: 'ordered-list', items: currentOrderedGroup });
      currentOrderedGroup = [];
    }
  };

  sections?.forEach((section, index) => {
    if (section.type === 'bullet') {
      if (currentOrderedGroup.length > 0 || currentSubBulletGroup.length > 0 || currentSubSubBulletGroup.length > 0) {
        flushGroups();
      }
      currentBulletGroup.push({ ...section, originalIndex: index });
    } else if (section.type === 'sub-bullet') {
      if (currentOrderedGroup.length > 0 || currentSubSubBulletGroup.length > 0) {
        if (currentSubSubBulletGroup.length > 0) {
          groupedSections.push({ type: 'sub-sub-bullet-group', items: currentSubSubBulletGroup });
          currentSubSubBulletGroup = [];
        }
      }
      if (currentBulletGroup.length > 0) {
        groupedSections.push({ type: 'bullet-group', items: currentBulletGroup });
        currentBulletGroup = [];
      }
      currentSubBulletGroup.push({ ...section, originalIndex: index });
    } else if (section.type === 'sub-sub-bullet') {
      if (currentBulletGroup.length > 0) {
        groupedSections.push({ type: 'bullet-group', items: currentBulletGroup });
        currentBulletGroup = [];
      }
      if (currentSubBulletGroup.length > 0) {
        groupedSections.push({ type: 'sub-bullet-group', items: currentSubBulletGroup });
        currentSubBulletGroup = [];
      }
      currentSubSubBulletGroup.push({ ...section, originalIndex: index });
    } else if (section.type === 'ordered-list') {
      if (currentBulletGroup.length > 0 || currentSubBulletGroup.length > 0 || currentSubSubBulletGroup.length > 0) {
        flushGroups();
      }
      currentOrderedGroup.push({ ...section, originalIndex: index });
    } else {
      flushGroups();
      groupedSections.push({ ...section, originalIndex: index });
    }
  });

  flushGroups();

  const renderedSections = groupedSections.map((section, index) => {
    switch (section.type) {
      case 'title-main':
        return (
          <Heading key={`title-${section.originalIndex}-${index}`}>
            {safeContent(section.content)}
          </Heading>
        );
      case 'title':
        return (
          <Title key={`title-${section.originalIndex}-${index}`}>
            {safeContent(section.content)}
          </Title>
        );

      case 'h3':
        return (
          <H3 key={`title-${section.originalIndex}-${index}`}>
            {safeContent(section.content)}
          </H3>
        );
      case 'subtitle':
        return (
          <Subtitle type="subtitle" key={`subtitle-${section.originalIndex}-${index}`}>
            {safeContent(section.content)}
          </Subtitle>
        );
      case 'text':
        return (
          <Text key={`text-${section.originalIndex}-${index}`}>
            {section.label && <strong>{section.label} </strong>}
            {safeContent(section.content)}
          </Text>
        );
      case 'bullet-group':
        return (
          <List key={`bullet-group-${index}`}>
            {section.items.map((item, itemIndex) => (
              <ListItem key={`bullet-${item.originalIndex}-${itemIndex}`}>
                {item.pill ? (
                  <>
                    {/* The label is the bullet's title, set like "Headline:" —
                        the pill beside it carries the numbers alone. */}
                    {item.label && <strong>{item.label} </strong>}
                    <AccuracyPill $tone={item.pill} title={item.tooltip || undefined}>{item.pillText}</AccuracyPill>
                    {item.tooltip && (
                      <>
                        {' '}
                        <Tooltip
                          id={`solo-accuracy-tooltip-${item.originalIndex}-${itemIndex}`}
                          tip={item.tooltip}
                          placement="top"
                        >
                          <InlineInfoIcon src={info2} alt="Info" />
                        </Tooltip>
                      </>
                    )}
                    {item.content ? <>{' '}{safeContent(item.content)}</> : null}
                  </>
                ) : item.tooltip ? (
                  <>
                    {item.label && <strong>{item.label}{item.tooltipAfterContent ? ':' : ''}</strong>}
                    {!item.tooltipAfterContent && (
                      <>
                        {' '}
                        <CollabLevelInfo id={`collab-level-tooltip-${item.originalIndex}-${itemIndex}`} />
                      </>
                    )}
                    {item.content ? <>{item.tooltipAfterContent ? ' ' : ': '}{safeContent(item.content)}</> : null}
                    {item.tooltipAfterContent && (
                      <>
                        {' '}
                        <CollabLevelInfo id={`collab-level-tooltip-${item.originalIndex}-${itemIndex}`} />
                      </>
                    )}
                  </>
                ) : (
                  renderContentWithBoldColon(item.content, item.label)
                )}
              </ListItem>
            ))}
          </List>
        );

      case 'answer-survey-table':
        return (
          <TableScroll key={`answer-survey-${section.originalIndex}-${index}`}>
            <AnswerTable>
              <caption className="sr-only">Answer and survey results</caption>
              <thead>
                <tr>
                  <th scope="col">Student</th>
                  <th scope="col">Solo Time Survey</th>
                  <th scope="col">Solo</th>
                  <th scope="col">Team Time Round 1</th>
                  <th scope="col">Team Time Round 2</th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => (
                  <tr key={row.studentName}>
                    <th scope="row">{row.studentName}</th>
                    <td>{row.survey || ''}</td>
                    <AnswerCell $correct={row.solo?.isCorrect}>
                      {row.solo?.answer || ''}
                      {row.solo && <AnswerMark correct={row.solo.isCorrect} />}
                    </AnswerCell>
                    <AnswerCell $correct={row.teamRound1?.isCorrect}>
                      {row.teamRound1?.answer || ''}
                      {row.teamRound1 && <AnswerMark correct={row.teamRound1.isCorrect} />}
                    </AnswerCell>
                    <AnswerCell $correct={row.teamRound2?.isCorrect}>
                      {row.teamRound2?.answer || ''}
                      {row.teamRound2 && <AnswerMark correct={row.teamRound2.isCorrect} />}
                    </AnswerCell>
                  </tr>
                ))}
              </tbody>
            </AnswerTable>
          </TableScroll>
        );

      case 'ordered-list':
        return (
          <OrderedList key={`ordered-list-${index}`}>
            {section.items.map((item, itemIndex) => (
              <OrderedItem key={`ordered-item-${item.originalIndex}-${itemIndex}`}>
                {item.label && <strong>{item.label} </strong>}
                {safeContent(item.content)}
              </OrderedItem>
            ))}
          </OrderedList>
        );
      case 'sub-bullet-group':
        return (
          <SubList key={`sub-bullet-group-${index}`}>
            {section.items.map((item, itemIndex) => (
              <SubListItem key={`sub-bullet-${item.originalIndex}-${itemIndex}`}>
                {renderContentWithBoldColon(item.content, item.label)}
                {item.tooltip && (
                  <CollabLevelInfo id={`collab-level-tooltip-${item.originalIndex}-${itemIndex}`} />
                )}
              </SubListItem>
            ))}
          </SubList>
        );
      case 'sub-sub-bullet-group':
        return (
          <SubSubList key={`sub-sub-bullet-group-${index}`}>
            {section.items.map((item, itemIndex) => (
              <SubSubListItem key={`sub-sub-bullet-${item.originalIndex}-${itemIndex}`}>
                {renderContentWithBoldColon(item.content, item.label)}
              </SubSubListItem>
            ))}
          </SubSubList>
        );
      case 'span':
        return (
          <Span key={`span-${section.originalIndex}-${index}`}>
            {safeContent(section.content)}
          </Span>
        );

      case 'title-hr':
        return (
          <TitleHr key={`title-hr-${section.originalIndex}-${index}`}>
            <Title color="#fd1e7e">
              {safeContent(section.content)}
            </Title>
          </TitleHr>
        );
      case 'incident-counts':
        // The lesson report card's own strip, reused so the two surfaces
        // cannot drift. No onViewIncidents (e.g. the admin console) hides the
        // View Details link.
        return (
          <IncidentCountsSummary
            key={`incident-counts-${section.originalIndex}-${index}`}
            counts={section.counts}
            onViewDetails={onViewIncidents}
            emptyText="No critical incidents were flagged for this session."
          />
        );
      case 'warning':
        return (
          <Warning key={`warning-${section.originalIndex}-${index}`}>
            {safeContent(section.content)}
          </Warning>
        );
      case 'image':
        return (
          <ImageContainer key={`image-${section.originalIndex}-${index}`}>
            <QuestionImage src={section.content} alt="Question" />
          </ImageContainer>
        );
      default:
        return null;
    }
  });

  if (!isIncomplete) return renderedSections;

  return (
    <>
      <IncompleteNotice role="status">{INCOMPLETE_REPORT_NOTICE}</IncompleteNotice>
      {renderedSections}
    </>
  );
};

const TitleHr = styled.div`
display: flex;
flex-direction: row;
gap: 1rem;
align-items: center;

h2 {
  margin: 0;
  padding: 0;
  width: 100%;
}

hr {
  width: 100%;
  border: 1px solid gray;
  height: 0.1rem;
  background-color: rgba(var(--color-foreground), 0.2);
}
`

const Heading = styled.h2`
  font-size: 1.75rem;
  font-weight: bold;
  margin-bottom: .75rem;
  color: #605dec;
  margin-top: 2.5rem;
`

const H3 = styled.h3`
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: .7rem;
`

const Span = styled.h3`
  font-size: .75rem;
  font-weight: bold;
  margin: .5rem 0 .25rem 0;
`

const List = styled.ul`
  list-style: disc;
  padding-left: 2.5rem;
  margin-bottom: 1rem;
`

const OrderedList = styled.ol`
  list-style: decimal;
  padding-left: 2.5rem;
  margin-bottom: 1rem;
`

const SubList = styled.ul`
  list-style: circle;
  padding-left: 5rem;
  margin-bottom: .5rem;
`

const SubListItem = styled.li`
  margin-bottom: .25rem;
  display: flex;
  align-items: flex-start;
  gap: 4px;

  strong {
    font-weight: 700;
  }
`

const IncompleteNotice = styled.p`
  background-color: ${colors.amber50 || "#FFFBEB"};
  border: 1px solid ${colors.amber700 || "#B45309"};
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 16px;
  padding: 12px 16px;
`

const SubSubList = styled.ul`
  list-style: square;
  padding-left: 7.5rem;
  margin-bottom: .5rem;
`

const SubSubListItem = styled.li`
  margin-bottom: .25rem;

  strong {
    font-weight: 700;
  }
`

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  padding: 1rem 0 .5rem;
  color: ${props => props.color || 'inherit'};
`

const Subtitle = styled.h4`
  font-size: 1.125rem;
  font-weight: bold;
  marginr 0;
`

const Text = styled.p`
  margin-bottom: .5rem;
  line-height: 1.5;
  color: ${props => props.color || 'inherit'};
  ${props => props.color && props.color !== 'inherit' && `font-weight: 600;`}

  strong {
    font-weight: 700;
  }
`

// Solo Accuracy pill. The tone comes from the accuracy against the same
// thresholds the backend uses to pick a math level, so the colour and the
// level name beside it can never disagree.
//
// The colours are the level badges' own: a red pill and a Level 1 badge mean
// the same thing on the same page, so they cannot be different colour families.
// Read on use rather than at module load: this file and the level badges sit on
// the same import cycle, so the palette is not yet populated at load time.
export const pillTones = () => ({
  green: toneOf(LEVEL_COLORS[3]),
  yellow: toneOf(LEVEL_COLORS[2]),
  red: toneOf(LEVEL_COLORS[1]),
});

const pillTone = ($tone) => {
  const tones = pillTones();
  return tones[$tone] || tones.yellow;
};

const AccuracyPill = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.85em;
  font-weight: 600;
  background: ${({ $tone }) => pillTone($tone).bg};
  color: ${({ $tone }) => pillTone($tone).fg};
  border: 1px solid ${({ $tone }) => pillTone($tone).border};
`;

// Answer & Survey Results table. Five columns do not fit a narrow viewport, so
// the table scrolls inside its own box rather than widening the report.
const TableScroll = styled.div`
  overflow-x: auto;
  margin: 8px 0 16px;
`;

const AnswerTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;

  th,
  td {
    padding: 6px 12px;
    text-align: left;
    border-bottom: 1px solid ${colors.grey200 || "#E5E7EB"};
    white-space: nowrap;
  }

  thead th {
    font-weight: 600;
    border-bottom-width: 2px;
  }

  tbody th {
    font-weight: 600;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

// A correct answer is tinted, but the answer itself still carries the meaning —
// colour alone never distinguishes right from wrong.
const AnswerCell = styled.td`
  color: ${({ $correct }) => {
    if ($correct === true) return colors.green700;
    if ($correct === false) return colors.orange800;
    return "inherit";
  }};
  font-weight: ${({ $correct }) => ($correct === true ? 600 : "inherit")};
`;

const ListItem = styled.li`
  margin-bottom: .25rem;

  strong {
    font-weight: 700;
  }
`


const OrderedItem = styled.li`
  margin-bottom: .25rem;

  strong {
    font-weight: 700;
  }
`

const Warning = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: .25rem;
  padding: .5rem;
  margin: .5rem 0;
  color: #856404;
`

const ImageContainer = styled.div`
  margin: 1rem 0 1rem 2.5rem;
`

const QuestionImage = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: .5rem;
  border: 1px solid #e5e7eb;
`
